import React from 'react';
import { FiMove, FiCopy, FiTrash2, FiLock, FiLayers } from 'react-icons/fi';
import useEditorStore from '../../../stores/useEditorStore';
import { ColorPicker } from '../../common';

const SelectSettingsPanel = () => {
  const { canvas, selectedObjects, pushHistory, syncLayersFromCanvas } = useEditorStore();

  const hasSelection = selectedObjects.length > 0;

  // 선택된 객체가 TableGroup인지 감지
  const selectedTableGroup = selectedObjects.length === 1
    && selectedObjects[0].type === 'group'
    && selectedObjects[0]._isTableGroup
    ? selectedObjects[0] : null;

  // 테이블 전체 셀 배경색 변경
  const handleTableAllCellBgChange = (color) => {
    if (!selectedTableGroup || !canvas) return;
    selectedTableGroup.getObjects().forEach(child => {
      if (child._isTableCell) {
        child.set({ fill: color });
      }
    });
    canvas.renderAll();
    pushHistory();
  };

  // 복제
  const handleDuplicate = async () => {
    if (!canvas || !hasSelection) return;

    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      try {
        const cloned = await activeObject.clone();
        cloned.set({
          left: cloned.left + 20,
          top: cloned.top + 20,
          id: `${cloned.type}-${Date.now()}`,
        });
        canvas.add(cloned);
        canvas.setActiveObject(cloned);
        canvas.renderAll();
        pushHistory();
      } catch (err) {
        console.error('Duplicate error:', err);
      }
    }
  };

  // 삭제
  const handleDelete = () => {
    if (!canvas || !hasSelection) return;

    const activeObjects = canvas.getActiveObjects();
    activeObjects.forEach(obj => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.renderAll();
    pushHistory();
  };

  // 잠금/해제
  const handleToggleLock = () => {
    if (!canvas || !hasSelection) return;

    const activeObjects = canvas.getActiveObjects();
    activeObjects.forEach(obj => {
      const isLocked = obj.selectable === false;
      obj.set({
        selectable: isLocked,
        evented: isLocked,
      });
    });
    canvas.discardActiveObject();
    canvas.renderAll();
    syncLayersFromCanvas();
  };

  // 맨 앞으로
  const handleBringToFront = () => {
    if (!canvas || !hasSelection) return;

    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.bringObjectToFront(activeObject);
      canvas.renderAll();
      pushHistory();
      syncLayersFromCanvas();
    }
  };

  // 맨 뒤로
  const handleSendToBack = () => {
    if (!canvas || !hasSelection) return;

    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.sendObjectToBack(activeObject);
      canvas.renderAll();
      pushHistory();
      syncLayersFromCanvas();
    }
  };

  // 앞으로
  const handleBringForward = () => {
    if (!canvas || !hasSelection) return;

    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.bringObjectForward(activeObject);
      canvas.renderAll();
      pushHistory();
      syncLayersFromCanvas();
    }
  };

  // 뒤로
  const handleSendBackward = () => {
    if (!canvas || !hasSelection) return;

    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.sendObjectBackwards(activeObject);
      canvas.renderAll();
      pushHistory();
      syncLayersFromCanvas();
    }
  };

  return (
    <div style={panelStyle}>
      <h3 style={titleStyle}>선택 도구</h3>

      {/* 선택 안내 */}
      <div style={sectionStyle}>
        <div style={{
          padding: '16px',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <FiMove size={32} color="#9ca3af" style={{ marginBottom: '8px' }} />
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
            {hasSelection
              ? `${selectedObjects.length}개 객체 선택됨`
              : '캔버스에서 객체를 클릭하여 선택하세요'}
          </p>
        </div>
      </div>

      {/* 테이블 전체 셀 배경색 */}
      {selectedTableGroup && (
        <div style={sectionStyle}>
          <label style={labelStyle}>테이블 설정</label>
          <div style={{
            padding: '12px',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #bbf7d0',
            marginBottom: '8px',
          }}>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#059669', fontWeight: '600' }}>
              {selectedTableGroup._tableRows}행 × {selectedTableGroup._tableCols}열 테이블
            </p>
            <p style={{ margin: 0, fontSize: '11px', color: '#6b7280' }}>
              더블클릭으로 개별 셀 편집
            </p>
          </div>
          <ColorPicker
            label="전체 셀 배경색"
            value={(() => {
              const firstCell = selectedTableGroup.getObjects().find(c => c._isTableCell);
              return firstCell?.fill || '#ffffff';
            })()}
            onChange={handleTableAllCellBgChange}
            showTransparent
            size="large"
          />
        </div>
      )}

      {/* 액션 버튼들 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>편집</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
          <button
            onClick={handleDuplicate}
            disabled={!hasSelection}
            style={actionButtonStyle(hasSelection)}
          >
            <FiCopy size={18} />
            <span>복제</span>
          </button>
          <button
            onClick={handleDelete}
            disabled={!hasSelection}
            style={{ ...actionButtonStyle(hasSelection), color: hasSelection ? '#ef4444' : '#d1d5db' }}
          >
            <FiTrash2 size={18} />
            <span>삭제</span>
          </button>
          <button
            onClick={handleToggleLock}
            disabled={!hasSelection}
            style={actionButtonStyle(hasSelection)}
          >
            <FiLock size={18} />
            <span>잠금</span>
          </button>
        </div>
      </div>

      {/* 레이어 순서 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>레이어 순서</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
          <button
            onClick={handleBringToFront}
            disabled={!hasSelection}
            style={actionButtonStyle(hasSelection)}
          >
            <FiLayers size={18} />
            <span>맨 앞으로</span>
          </button>
          <button
            onClick={handleSendToBack}
            disabled={!hasSelection}
            style={actionButtonStyle(hasSelection)}
          >
            <FiLayers size={18} style={{ transform: 'rotate(180deg)' }} />
            <span>맨 뒤로</span>
          </button>
          <button
            onClick={handleBringForward}
            disabled={!hasSelection}
            style={actionButtonStyle(hasSelection)}
          >
            <span>앞으로</span>
          </button>
          <button
            onClick={handleSendBackward}
            disabled={!hasSelection}
            style={actionButtonStyle(hasSelection)}
          >
            <span>뒤로</span>
          </button>
        </div>
      </div>

    </div>
  );
};

const panelStyle = {
  padding: '16px',
  height: '100%',
  overflowY: 'auto',
};

const titleStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#1f2937',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '1px solid #e5e7eb',
};

const sectionStyle = {
  marginBottom: '20px',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '8px',
};

const actionButtonStyle = (enabled) => ({
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  cursor: enabled ? 'pointer' : 'not-allowed',
  color: enabled ? '#374151' : '#d1d5db',
  fontSize: '12px',
  opacity: enabled ? 1 : 0.5,
});

export default SelectSettingsPanel;
